/**
 * The kiosk's state.
 *
 * The six-step machine from `docs/screens.md`, the cart, and the small number
 * of facts a screen needs that are not server state. Server state is TanStack
 * Query's; this holds only what the customer is doing right now.
 *
 * The cart lives here rather than on the server — see D4. `@mgc/domain` prices
 * it, and the server re-prices the same lines when the order is posted.
 *
 * **There is no `selectedCategory` any more.** It was here because the menu
 * showed one category at a time and the choice had to survive a re-render; the
 * menu is now one continuous scroller and the heading a customer is under is a
 * fact about a scroll position, not about the order they are placing. Keeping
 * it in the store would also have meant writing to zustand on every scroll
 * frame, which re-renders every tile on the screen.
 */
import { useMemo } from 'react'
import { create } from 'zustand'
import type { Cart, Catalogue, Menu, Money, OrderType, Product, Sku, Temperature } from '@mgc/domain'
import {
  addLine,
  catalogueOf,
  emptyCart,
  priceCart,
  removeLine,
  setLineOrderType,
  setOrderType,
  setQuantity
} from '@mgc/domain'
import type { Locale } from '@mgc/i18n'
import { DEFAULT_LOCALE } from '@mgc/i18n'

export type Step = 'welcome' | 'order-type' | 'menu' | 'checkout' | 'payment' | 'success'

/**
 * What the terminal did, kept for the paper.
 *
 * The slip's payment line is built from the server's view of the order, and an
 * offline sale has no such view — so `payments: view?.payments ?? []` printed
 * **nothing** on the one receipt whose whole job is to prove that money moved.
 * A customer with no uplink walked away holding a slip that named drinks, a
 * total and no payment at all, to a counter that could not look the sale up
 * either.
 *
 * This is the kiosk's own copy, written the moment a charge is captured and
 * read by the success screen when the server cannot answer.
 */
export interface Capture {
  readonly method: 'cash' | 'card' | 'qpay'
  readonly amount: Money
  /** The terminal's approval code. What a cashier reconciles against a batch. */
  readonly reference: string | null
}

export interface AddIntent {
  readonly productId: Sku
  readonly quantity: number
  readonly temperature?: Temperature
  readonly changeCode?: Sku
  readonly addCodes: readonly Sku[]
  readonly note?: string
}

export interface KioskState {
  step: Step
  locale: Locale
  cart: Cart
  menu: Menu | null
  /** How old the cached menu is. Past a threshold the screen says so. */
  menuServedAt: string | null
  branchClosed: boolean
  online: boolean
  openProduct: Product | null
  /** Lines dropped by a catalogue refresh, so the screen can say which. */
  removed: readonly string[]
  orderId: string | null
  /**
   * An order that has reserved stock at 1C and has not been paid for.
   *
   * Set the moment a payment is attempted, cleared when one succeeds. It is
   * what lets the idle timer and the back button hand the reservation back
   * instead of leaving it to time out — the payment screen is unmounted by then
   * and cannot do it itself.
   */
  pendingOrderId: string | null
  /** The charge behind `orderId`, for a receipt the server could not describe. */
  capture: Capture | null
  lastRefusal: string | null
  /**
   * Money is moving *right now* — a terminal is authorising, a QR is being
   * settled, an order is posting.
   *
   * Distinct from the update gate's `paymentInFlight`, which the payment screen
   * holds for its whole lifetime so a bundle cannot swap under it. This one is
   * true only while a charge is actually outstanding, because the two questions
   * have different answers and only one of them may stop the idle timer: a
   * customer who reaches the payment screen and walks away must still be
   * cleared, and a customer whose card is in the terminal must never be.
   */
  paying: boolean
}

export interface KioskActions {
  setStep: (step: Step) => void
  setLocale: (locale: Locale) => void
  setMenu: (menu: Menu, servedAt: string) => void
  setOnline: (online: boolean) => void
  setBranchClosed: (closed: boolean) => void
  openProductModal: (product: Product | null) => void
  chooseOrderType: (orderType: OrderType) => void
  add: (intent: AddIntent) => { ok: boolean; reason?: string }
  setLineQuantity: (lineId: string, quantity: number) => { ok: boolean; reason?: string }
  removeCartLine: (lineId: string) => void
  setLineOrder: (lineId: string, orderType: OrderType | undefined) => void
  setOrderId: (id: string | null) => void
  setPendingOrderId: (id: string | null) => void
  setCapture: (capture: Capture | null) => void
  setPaying: (paying: boolean) => void
  setRefusal: (refusal: string | null) => void
  clearRemoved: () => void
  reset: () => void
  catalogue: () => Catalogue
}

const empty: KioskState = {
  step: 'welcome',
  locale: DEFAULT_LOCALE,
  cart: emptyCart,
  menu: null,
  menuServedAt: null,
  branchClosed: false,
  online: true,
  openProduct: null,
  removed: [],
  orderId: null,
  pendingOrderId: null,
  capture: null,
  lastRefusal: null,
  paying: false
}

/**
 * Line ids are minted here rather than inside `@mgc/domain`.
 *
 * A pure function that calls `crypto.randomUUID()` is not one, and a cart that
 * replays differently on the server than it did on the page is a cart nobody
 * can reconcile.
 */
let counter = 0
const newLineId = (): string => {
  counter += 1
  return `L${Date.now().toString(36)}${counter.toString(36)}`
}

export const useKiosk = create<KioskState & KioskActions>()((set, get) => ({
  ...empty,

  setStep: (step) => set({ step }),
  setLocale: (locale) => set({ locale }),
  setOnline: (online) => set({ online }),
  setBranchClosed: (branchClosed) => set({ branchClosed }),
  openProductModal: (openProduct) => set({ openProduct }),
  setOrderId: (orderId) => set({ orderId }),
  setPendingOrderId: (pendingOrderId) => set({ pendingOrderId }),
  setCapture: (capture) => set({ capture }),
  setPaying: (paying) => set({ paying }),
  setRefusal: (lastRefusal) => set({ lastRefusal }),
  clearRemoved: () => set({ removed: [] }),

  catalogue: () => catalogueOf(get().menu?.products ?? []),

  setMenu: (menu, servedAt) => {
    const catalogue = catalogueOf(menu.products)
    const priced = priceCart(get().cart, catalogue)
    // A product that left the catalogue mid-order is dropped and named, not
    // left in a cart that will be refused at the till.
    const dropped = priced.dropped.map((l) => l.id)
    const cart =
      dropped.length === 0
        ? get().cart
        : { ...get().cart, lines: get().cart.lines.filter((l) => !dropped.includes(l.id)) }

    set({
      menu,
      menuServedAt: servedAt,
      branchClosed: menu.branchClosed,
      cart,
      removed: dropped
    })
  },

  chooseOrderType: (orderType) => {
    const { cart, removed } = setOrderType(get().cart, get().catalogue(), orderType)
    set({ cart, removed: [...get().removed, ...removed.map((l) => l.id)], step: 'menu' })
  },

  add: (intent) => {
    const result = addLine(
      get().cart,
      get().catalogue(),
      {
        productId: intent.productId,
        quantity: intent.quantity,
        ...(intent.temperature === undefined ? {} : { temperature: intent.temperature }),
        ...(intent.changeCode === undefined ? {} : { changeCode: intent.changeCode }),
        addCodes: intent.addCodes,
        ...(intent.note === undefined ? {} : { note: intent.note })
      },
      newLineId()
    )
    if (!result.ok) return { ok: false, reason: result.refusal.reason }
    set({ cart: result.cart, openProduct: null })
    return { ok: true }
  },

  setLineQuantity: (lineId, quantity) => {
    const result = setQuantity(get().cart, get().catalogue(), lineId, quantity)
    if (!result.ok) return { ok: false, reason: result.refusal.reason }
    set({ cart: result.cart })
    return { ok: true }
  },

  removeCartLine: (lineId) => set({ cart: removeLine(get().cart, lineId) }),

  setLineOrder: (lineId, orderType) =>
    set({ cart: setLineOrderType(get().cart, get().catalogue(), lineId, orderType) }),

  /**
   * Back to the welcome screen, keeping what belongs to the machine rather than
   * to the customer: the menu, the connection, the branch.
   *
   * The locale used to be kept here too, from when nothing could change it and
   * it was a property of the installation. Now that a customer can switch it,
   * it is theirs — and leaving it set would hand the next person at the panel a
   * menu in a language they did not choose. The old Electron app persisted it
   * to `localStorage`, which had exactly that effect until somebody switched it
   * back.
   */
  reset: () =>
    set((state) => ({
      ...empty,
      menu: state.menu,
      menuServedAt: state.menuServedAt,
      branchClosed: state.branchClosed,
      online: state.online
    }))
}))

/**
 * The priced cart, for a screen that needs the lines and the total.
 *
 * Memoised, because `catalogueOf` builds a `Map` over the whole branch
 * catalogue and `priceCart` re-prices every line — and the menu screen that
 * calls this also owns the search box, so without the memo every keystroke
 * repriced the basket. It was a sibling component's problem until the basket
 * rail became a bar inside `Menu` (D26).
 */
export function usePricedCart() {
  const cart = useKiosk((s) => s.cart)
  const menu = useKiosk((s) => s.menu)
  return useMemo(() => priceCart(cart, catalogueOf(menu?.products ?? [])), [cart, menu])
}
